/**
 * PluginRenderer — Renders a plugin arche in a sandboxed iframe.
 *
 * Responsibilities:
 * - Fetches plugin config from the arche record
 * - Creates sandboxed iframe with strict CSP
 * - Establishes postMessage bridge via PluginBridgeHost
 * - Handles resize, navigation, and toast events
 * - Shows loading/error states
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '@/auth/hooks/useAuth';
import api, { API_BASE, API_ORIGIN } from '@/lib/api';
import { authApi } from '@/lib/auth-api';
import { toast } from '@/components/ui/use-toast';
import { addBreadcrumb } from '@/lib/telemetry';
import { isApiErrorCode } from '@/lib/api-error-code';
import { useRegisterArcheAI, useArcheHandoffIntake } from '@/components/fias-ai';
import { ARCHE_DOCUMENT_INTAKE, type ArcheHandoffPayload } from '@/components/fias-ai/handoff';
import { useGlobalFiasAIOptional } from '@/components/fias-ai/hooks/GlobalFiasAIContext';
import type {
  ArcheAIAction,
  PluginDeclaredAiAction,
  PluginRuntimeAiDeclaration,
} from '@/components/fias-ai/types';
import { PluginBridgeHost } from './PluginBridge';
import { PLUGIN_IFRAME_ALLOW, PLUGIN_IFRAME_SANDBOX } from './pluginSandbox';
import { PurchaseConfirmationSheet } from './PurchaseConfirmationSheet';
import { PLUGIN_STORE_ENTITY_ID } from '@/lib/integration-entity-permissions';
import { VaultAccessGrantSheet, type VaultPickResolution } from './VaultAccessGrantSheet';
import {
  VaultSaveConsentSheet,
  type VaultSaveRequest,
  type VaultSaveResolution,
} from './VaultSaveConsentSheet';
import { ArcheLoadingScreen } from './ArcheLoadingScreen';
import { FiasAIDiscoveryChip } from './FiasAIDiscoveryChip';
import { DARK_THEME, LIGHT_THEME, getThemeById, type PluginPermission } from '@fias/arche-sdk';
import { normalizeCdnBaseUrl, allVendoredLibraryAssets } from '@fias/platform-resources';

/**
 * Resolve an `open_arche` bridge request host-side: navigate the top window to
 * another arche's page (`/a/<archeId>`), optionally at a sub-path
 * (`/a/<archeId>/<path>`) so the opener can land the user on a specific page of
 * the target's own router instead of its home screen. `newTab` is best-effort —
 * the parent's `window.open` can be popup-blocked because user activation does
 * not cross the iframe→host postMessage boundary, so we fall back to same-tab
 * navigation when the popup is blocked (returns null) and the click never
 * silently no-ops.
 *
 * `targetArcheId` and `opts.path` are already validated by the bridge before
 * this runs. `windowOpen` is injectable for testing. Exported for unit tests.
 */
export function openArcheFromHost(
  navigate: (path: string) => void,
  targetArcheId: string,
  opts: { newTab?: boolean; path?: string },
  windowOpen: (url: string, target: string, features: string) => Window | null = (url, t, f) =>
    window.open(url, t, f),
): void {
  // The bridge has already validated both halves; this only joins them. An
  // options object rather than more positional args so the `windowOpen` seam
  // (which every test overrides) stays last.
  const url = opts.path ? `/a/${targetArcheId}/${opts.path}` : `/a/${targetArcheId}`;
  if (opts.newTab) {
    const opened = windowOpen(url, '_blank', 'noopener,noreferrer');
    if (!opened) navigate(url);
  } else {
    navigate(url);
  }
}

interface PluginRendererProps {
  archeId: string;
  /**
   * `arches.is_trusted` — threaded from ArcheInteractionPage's metadata fetch
   * (the `getArche` response this component fetches does not carry it). Gates
   * the RUNTIME Fias AI declaration lane: non-trusted plugins are
   * manifest-only, because manifest action text is fixed at publish and passes
   * submission review, while runtime text is never reviewed. Defaults false
   * (fail closed).
   */
  isTrusted?: boolean;
}

/** Matches PluginConfig from @fias/db-types (redefined for client use) */
interface PluginConfig {
  bundleRef: string;
  manifestVersion: string;
  // Full client-safe permission union from the SDK (lockstep with db-types) —
  // see the note in PluginBridge.ts. Avoids the prior hand-listed subset that
  // omitted most scopes.
  permissions: PluginPermission[];
  sdkVersion: string;
  bundleSizeBytes: number;
  buildHash: string;
  themeId?: string;
  fontPairingId?: string;
  customTheme?: import('@fias/arche-sdk').FiasTheme;
  /** Manifest `fiasAI` block snapshotted at publish (null = explicitly none). */
  fiasAI?: { description: string; actions: PluginDeclaredAiAction[] } | null;
}

/**
 * Hard cap on the MERGED (manifest ∪ runtime) action list — the assistant
 * turn endpoint's `.strict()` schema rejects >50 actions, which would 400
 * every turn while the plugin is open. Manifest actions win the trim
 * (deterministic, and they passed publish review).
 */
const MAX_MERGED_AI_ACTIONS = 50;

/** After the user closes or minimizes the assistant panel, plugin `ai_open`
 * requests are ignored for this long — the user's dismissal outranks the
 * plugin's desire to reopen (bridge-level burst debounce is separate). */
const AI_OPEN_AFTER_DISMISS_COOLDOWN_MS = 30_000;

/** availableWhen: dumb equality against the plugin's last state snapshot. */
function aiActionAvailable(
  action: PluginDeclaredAiAction,
  state: Record<string, unknown>,
): boolean {
  if (!action.availableWhen) return true;
  return Object.entries(action.availableWhen).every(([key, value]) => state[key] === value);
}

/**
 * Strip to EXACTLY the fields the assistant turn schema accepts —
 * `availableWhen` (or any future host-only field) leaking through would 400
 * the whole turn (`archeActionSchema` is `.strict()`).
 */
function toArcheAIAction(a: PluginDeclaredAiAction): ArcheAIAction {
  return {
    id: a.id,
    description: a.description,
    parameterSchema: a.parameterSchema,
    ...(a.requiresConfirmation !== undefined
      ? { requiresConfirmation: a.requiresConfirmation }
      : {}),
    ...(a.category !== undefined ? { category: a.category } : {}),
  };
}

export function PluginRenderer({ archeId, isTrusted = false }: PluginRendererProps) {
  const [pluginConfig, setPluginConfig] = useState<PluginConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeHeight, setIframeHeight] = useState<number | null>(null);
  const [srcdoc, setSrcdoc] = useState<string | null>(null);
  const [iframeReady, setIframeReady] = useState(false);
  // The plugin's SDK has answered `init` with `ready`. Distinct from
  // `iframeReady` (the DOM load event): between the two, a postMessage lands in
  // a frame with no listener. Anything pushed one-way waits for THIS.
  const [pluginReady, setPluginReady] = useState(false);
  // No latest-ref mirror needed: readiness now lives on the bridge
  // (`handoffReadiness()`), which `deliverHandoff` reads through `bridgeRef` at
  // call time — so there is no stale state to close over.
  // When the plugin's SDK announces a protocol version below what the
  // host requires, we capture the version pair and render an "update
  // needed" affordance instead of leaving a misbehaving iframe mounted.
  const [incompatibleSdk, setIncompatibleSdk] = useState<{
    sdkVersion: number;
    minRequired: number;
  } | null>(null);
  // A retired op-family alias (storage_*/data_*/workspace_*/store_*) arrived
  // from an old, immutable bundle (ADR-012 Phase L Job 3 Step 2). Same "needs
  // update" treatment as an incompatible SDK, but triggered on first use of the
  // retired capability rather than at the init handshake.
  const [retiredCapability, setRetiredCapability] = useState<string | null>(null);

  // Purchase confirmation state
  const [purchaseProduct, setPurchaseProduct] = useState<{
    productIdentifier: string;
    name: string;
    description: string | null;
    priceCredits: number;
    type: string;
  } | null>(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [userBalance, setUserBalance] = useState<number | null>(null);
  const purchaseResolveRef = useRef<((value: unknown) => void) | null>(null);

  // Arche identity for the host consent sheets, captured from the mount fetch
  // (see loadPlugin) so a sheet never opens missing the facts it exists to show.
  const [archeDisplayName, setArcheDisplayName] = useState<string | null>(null);
  const [vaultFolderLabel, setVaultFolderLabel] = useState<string | null>(null);

  // Vault user-document picker state (vault_documents_pick host mediation)
  const [vaultPickRequest, setVaultPickRequest] = useState<{ maxDocuments: number } | null>(null);
  const vaultPickResolveRef = useRef<((value: VaultPickResolution) => void) | null>(null);

  // A handoff that arrived before the iframe finished its bridge handshake.
  // The `open_document` navigation path lands here on nearly every cold open
  // (we mount, then the iframe loads), so without the queue the common case —
  // "open this attachment in the editor" from a cold start — would silently
  // do nothing. Latest-wins: a second handoff supersedes an undelivered first.
  const pendingHandoffRef = useRef<ArcheHandoffPayload | null>(null);
  // Monotonic delivery ticket. Only the newest handoff may dispatch, so an
  // out-of-order grant completion cannot open a superseded document.
  const handoffSeqRef = useRef(0);

  // User-visible save state (the `userVisible` half of vault_documents_upload).
  // `vaultSaveFolderLabel` is the `/My-Fias/<label>/` destination, fetched with
  // the arche metadata so the sheet can name where the file goes; null when the
  // arche has no registered folder (the server refuses such a save anyway).
  const [vaultSaveRequest, setVaultSaveRequest] = useState<VaultSaveRequest | null>(null);
  const vaultSaveResolveRef = useRef<((value: VaultSaveResolution) => void) | null>(null);

  // Fias AI plane: the plugin's runtime action declaration (trusted arches
  // only) + its last state snapshot. Both are React state — the registered
  // capabilities object is rebuilt per render and read fresh at turn time, so
  // a ref-only snapshot would hand the assistant a stale availableWhen filter.
  const [runtimeAiDecl, setRuntimeAiDecl] = useState<PluginRuntimeAiDeclaration | null>(null);
  const [aiState, setAiState] = useState<Record<string, unknown>>({});
  // Latest-ref mirror for the synchronous getStateSnapshot contract.
  const aiStateRef = useRef(aiState);
  aiStateRef.current = aiState;
  // The bridge is constructed once per iframe load; read trust via ref so the
  // callback can't capture a stale value (same idiom as themeModeRef).
  const isTrustedRef = useRef(isTrusted);
  isTrustedRef.current = isTrusted;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const bridgeRef = useRef<PluginBridgeHost | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme: appTheme } = useTheme();
  const themeMode = appTheme === 'light' ? 'light' : 'dark';
  // The bridge's `getTheme` callback must read the CURRENT mode through this
  // ref, never the `themeMode` binding above. The bridge is constructed once,
  // at iframe load, so a plain closure captures the mode of THAT render; every
  // later `sendThemeUpdate()` then re-evaluates `getTheme` inside the stale
  // closure and pushes the ORIGINAL theme — the plugin re-renders with the
  // theme it already had, and the toggle appears dead until a hard reload
  // rebuilds the bridge. (`handleIframeLoad` lists `themeMode` in its deps,
  // but a fresh callback identity is useless: the iframe never fires `load`
  // again, so the fresh closure is never invoked.)
  //
  // Written during render (the latest-ref idiom) rather than in an effect, on
  // purpose: an effect version would tie correctness to effect ORDERING — the
  // sendThemeUpdate effect below must observe the new value in the same
  // commit — which is a subtler dependency than an idempotent render write.
  // Same idiom as the builder page's previewThemeRef and panel-kit's queueRef.
  const themeModeRef = useRef(themeMode);
  themeModeRef.current = themeMode;
  // Same latest-ref treatment for the user: `getUser` used to close over
  // `user` with the identical trap (listed in the callback deps, but the
  // iframe never re-fires `load`, so a fresh closure never installs). User
  // identity is stable within a session so it was latent rather than
  // user-visible — fixed for uniformity so the bridge options carry ONE idiom,
  // not a mix of live refs and stale closures.
  const userRef = useRef(user);
  userRef.current = user;

  // Fetch arche data to get plugin config
  useEffect(() => {
    let mounted = true;

    // Reset per-arche state FIRST. The effect is keyed on `archeId` but the
    // component is reused across arche-to-arche navigation, so without this the
    // previous plugin's `srcdoc` and `pluginConfig` stay mounted while the next
    // one loads — rendering arche A's bundle under arche B's permission grant.
    setLoading(true);
    setError(null);
    setSrcdoc(null);
    setPluginConfig(null);
    setIframeReady(false);
    setPluginReady(false);
    setIncompatibleSdk(null);
    setRetiredCapability(null);
    // Per-arche host state must reset with the arche, for the same reason the
    // bundle does. A handoff queued for arche A would otherwise be flushed into
    // arche B's iframe when B reports ready — handing the document reference
    // the user aimed at A to a different plugin entirely. An open save sheet
    // would keep showing A's file name and A's folder over B, and confirming it
    // would resolve a promise belonging to a bridge that is already destroyed.
    // Retire the OUTGOING arche's bridge here, not when the next iframe
    // happens to load. Left alive it is still `bridgeRef.current`, so a late or
    // repeated `ready` from arche A could flip `pluginReady` for arche B and a
    // handoff queued for B would be dispatched through A's bridge — into the
    // wrong plugin. Destroying it also settles anything A had pending.
    bridgeRef.current?.destroy();
    bridgeRef.current = null;
    pendingHandoffRef.current = null;
    // Supersede any grant still in flight for the outgoing arche — its
    // completion must not dispatch into the incoming one.
    handoffSeqRef.current += 1;
    setArcheDisplayName(null);
    setVaultFolderLabel(null);
    // Settle, don't drop: whoever is awaiting these is owed an answer.
    vaultSaveResolveRef.current?.({ canceled: true });
    vaultSaveResolveRef.current = null;
    setVaultSaveRequest(null);
    vaultPickResolveRef.current?.({ canceled: true });
    vaultPickResolveRef.current = null;
    setVaultPickRequest(null);

    async function loadPlugin() {
      try {
        const result = (await api.getArche(archeId)) as Record<string, unknown>;
        // `mounted` guards these for the same reason it guards the bundle
        // below, and it matters MORE here: on an A→B switch where A's request
        // finishes last, an unguarded write would label B's consent sheet with
        // A's name and A's folder while the server writes to B's. A consent
        // screen naming the wrong app and the wrong destination is worse than
        // one that names neither.
        //
        // Captured here rather than re-fetched per request: this endpoint
        // already returns both, and fetching them when the sheet opens paints
        // an empty destination line that fills in a beat later — long enough
        // for a quick confirm to approve without ever seeing where the file goes.
        if (!mounted) return;
        setArcheDisplayName((result.name as string | undefined) ?? null);
        setVaultFolderLabel((result.userVaultFolderLabel as string | undefined) ?? null);
        const config = result.pluginConfig as PluginConfig | null;

        if (!config) {
          throw new Error('This arche does not have a valid plugin configuration');
        }

        // EVERY bundle is fetched through the API proxy — there is deliberately
        // no direct-load branch. The proxy resolves the bundle server-side from
        // `archeId` alone (`bundleRef` is never sent), so a malformed or
        // hostile `bundleRef` cannot steer the browser at a third-party origin.
        // The previous `https://`-and-not-S3 escape hatch was unreachable (the
        // publish paths only ever write `plugin-bundles/…` or `s3://…`) and
        // failed OPEN, which is the wrong direction for a sandbox boundary.
        //
        // credentials: 'include' is required so the fias_session cookie flows
        // to the API on production (frontend and API live on different
        // subdomains; default fetch drops cookies on cross-origin). Without
        // it, signed-in users hit 401 on non-public plugins because
        // optionalAuth sees no token. Same pattern as api.ts:181.
        // Cache-bust on the build hash (defence in depth over the server's
        // ETag): the bundle URL is otherwise version-stable, so a stale
        // HTTP-cache entry from a prior publish could be reused even if an
        // intermediary ignores the response's Cache-Control. The hash is
        // fetched in this same effect run, so it always matches the config
        // we're about to render. NOTE: this only affects the NEXT mount /
        // navigation / reload — the effect is keyed on [archeId] and never
        // re-runs for an already-mounted plugin when a new version publishes.
        const bundleUrl = config.buildHash
          ? `${API_BASE}/arches/${archeId}/plugin-bundle?h=${encodeURIComponent(config.buildHash)}`
          : `${API_BASE}/arches/${archeId}/plugin-bundle`;
        const resp = await fetch(bundleUrl, {
          credentials: 'include',
        });
        if (!resp.ok) {
          const body = await resp.json().catch(() => null);
          const msg =
            (body as Record<string, Record<string, string>> | null)?.error?.message ||
            `Failed to load plugin bundle (${resp.status})`;
          throw new Error(msg);
        }
        const html = await resp.text();
        // The bundle was built with `__FIAS_API_BASE__` placeholders in
        // `<base href>` and the CSP `img-src` / `media-src` / `font-src`
        // allowlists. The placeholder is mis-named historically — it's
        // only used for plugin-asset URLs (the bridge call goes via
        // postMessage; the iframe CSP is `connect-src 'none'`).
        //
        // Prefer the dedicated plugin-assets CDN when configured —
        // CloudFront caches the immutable `/v1/arches/<id>/plugin-
        // assets/<version>/<file>` paths so repeated viewers don't hit
        // the API tier for each asset. Falls back to the API origin
        // when the env var is empty (local dev + envs before the CDN
        // stack deploys). Either origin serves the same path
        // (CloudFront forwards uncached requests to the same route),
        // so changing the substitution origin is a strict caching
        // optimization with no semantic difference.
        const pluginAssetsCdnBase = import.meta.env.VITE_PLUGIN_ASSETS_CDN_BASE as
          | string
          | undefined;
        const pluginAssetsOrigin =
          pluginAssetsCdnBase && pluginAssetsCdnBase.length > 0
            ? pluginAssetsCdnBase.replace(/\/$/, '')
            : API_ORIGIN;
        const substituted = html.split('__FIAS_API_BASE__').join(pluginAssetsOrigin);
        if (mounted) {
          setSrcdoc(substituted);
          setPluginConfig(config);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load plugin');
          setLoading(false);
        }
      }
    }

    loadPlugin();
    return () => {
      mounted = false;
    };
  }, [archeId]);

  // Set up bridge when iframe loads
  const handleIframeLoad = useCallback(() => {
    if (!iframeRef.current || !pluginConfig) return;

    setIframeReady(true);

    // Clean up previous bridge
    bridgeRef.current?.destroy();

    bridgeRef.current = new PluginBridgeHost({
      archId: archeId,
      permissions: pluginConfig.permissions,
      iframe: iframeRef.current,
      onResize: (height) => setIframeHeight(height),
      onToast: (message, variant) => {
        toast({
          description: message,
          variant: variant === 'error' ? 'destructive' : undefined,
        });
      },
      onNavigate: (path) => {
        navigate(path);
      },
      onOpenArche: (targetArcheId, opts) => openArcheFromHost(navigate, targetArcheId, opts),
      // Runtime Fias AI declaration — TRUSTED arches only (manifest text is
      // publish-reviewed; runtime text is not). The bridge has already
      // enforced permission + bounds; trust is the renderer's to decide
      // because it holds the metadata.
      onAiActionsDeclare: (declaration) => {
        if (!isTrustedRef.current) {
          addBreadcrumb('plugin-ai', 'runtime ai_actions_declare ignored (arche not trusted)', {
            archeId,
          });
          return;
        }
        setRuntimeAiDecl(declaration);
      },
      onAiState: (state) => setAiState(state),
      onAiOpenRequest: () => {
        const ctx = fiasAIGlobalRef.current;
        if (!ctx) return;
        if (ctx.isPanelOpen && !ctx.isMinimized) return; // already visible
        // A MINIMIZED panel is a standing dismissal, not a gap to jump
        // through: the user parked it (possibly before this renderer even
        // mounted, where the falling-edge tracker below can't have seen the
        // transition). Expanding it is the plugin overriding the user.
        if (ctx.isMinimized) {
          addBreadcrumb('plugin-ai', 'ai_open ignored (assistant is minimized by the user)', {
            archeId,
          });
          return;
        }
        if (Date.now() - lastPanelDismissAtRef.current < AI_OPEN_AFTER_DISMISS_COOLDOWN_MS) {
          addBreadcrumb('plugin-ai', 'ai_open ignored (user recently dismissed the panel)', {
            archeId,
          });
          return;
        }
        ctx.openPanel();
      },
      getTheme: () => {
        if (pluginConfig.customTheme) return pluginConfig.customTheme;
        if (pluginConfig.themeId) {
          const entry = getThemeById(pluginConfig.themeId);
          if (entry) return entry.theme;
        }
        // Via the ref, NOT the closure — see themeModeRef above.
        return themeModeRef.current === 'dark' ? DARK_THEME : LIGHT_THEME;
      },
      // Self-hosted vendored-library asset URLs (only consulted for
      // `sandbox:vendored-libraries` plugins). Resolved from the client's
      // platform-resources CDN env; empty when unconfigured (local dev / pre-CDN
      // staging) → plugin degrades. The full registry is injected; the CSP
      // (widened per declared dep at build time) is what gates actual loading.
      getVendoredAssets: () =>
        allVendoredLibraryAssets(
          normalizeCdnBaseUrl(
            import.meta.env.VITE_PLATFORM_RESOURCES_CDN_DOMAIN as string | undefined,
          ),
        ),
      getUser: async () => ({
        // Via the ref, NOT the closure — see userRef above.
        userId: userRef.current?.userId ?? '',
        displayName: userRef.current?.username ?? 'User',
        avatar: null,
      }),
      bridgeApiUrl: API_BASE,
      // Plugin iframes have an opaque origin and cannot send the
      // parent's session cookie. They authenticate to the bridge with
      // a per-arche capability JWT (audience=plugin-bridge, 5min TTL,
      // archeId-scoped). The token has NO authority outside the
      // /v1/plugins/bridge/:archId route — a leak cannot drain credits
      // or read vault data via any other endpoint.
      getAuthToken: async () => (await authApi.getPluginBridgeToken(archeId)) ?? '',
      // One-shot expired-token recovery (see callBackendOp): bust the cached
      // token so the retry mints fresh instead of re-sending the stale one.
      onAuthTokenExpired: () => authApi.invalidatePluginBridgeToken(archeId),
      onIncompatibleSdk: (sdkVersion, minRequired) => {
        setIncompatibleSdk({ sdkVersion, minRequired });
      },
      onRetiredCapability: (op) => {
        setRetiredCapability(op);
      },
      onPurchaseRequest: (product) => {
        return new Promise((resolve) => {
          // Fetch user balance for the confirmation sheet
          api
            .get<{ balance: number }>('/credits/balance')
            .then((res) => setUserBalance(res.data?.balance ?? null))
            .catch(() => setUserBalance(null));
          setPurchaseProduct(product);
          purchaseResolveRef.current = resolve;
        });
      },
      onVaultPickRequest: (request) => {
        return new Promise<VaultPickResolution>((resolve) => {
          setVaultPickRequest(request);
          vaultPickResolveRef.current = resolve;
        });
      },
      onPluginReady: () => setPluginReady(true),
      onVaultSaveRequest: (request) => {
        // One sheet at a time. Overwriting the resolver would strand the FIRST
        // save's promise forever — the plugin would sit on a pending await with
        // no error and no sheet, which for an editor saving two files is a
        // plausible sequence rather than an exotic one. Decline the newcomer
        // instead: `SAVE_DECLINED` is a documented outcome the caller already
        // handles, and the save the user is currently looking at still settles.
        if (vaultSaveResolveRef.current) {
          return Promise.resolve<VaultSaveResolution>({ canceled: true });
        }
        return new Promise<VaultSaveResolution>((resolve) => {
          setVaultSaveRequest(request);
          vaultSaveResolveRef.current = resolve;
        });
      },
    });

    bridgeRef.current.init();
    // NOTE: `themeMode` and `user` are deliberately NOT deps — `getTheme` and
    // `getUser` read the live values through refs. Listing them only churns
    // the callback identity, which cannot help (the iframe's `load` event has
    // already fired) and previously disguised the stale-closure bug as fixed.
  }, [archeId, pluginConfig, navigate]);

  // Send theme update when mode changes
  useEffect(() => {
    bridgeRef.current?.sendThemeUpdate();
  }, [themeMode]);

  // Fias AI plane — declarations are per-plugin; drop them when the arche
  // swaps (the bridge is torn down with the old iframe alongside).
  useEffect(() => {
    setRuntimeAiDecl(null);
    setAiState({});
  }, [archeId]);

  const manifestAi = pluginConfig?.fiasAI ?? null;

  // `ai_open` (discoverability Part 2): the bridge debounces bursts; the
  // renderer owns panel-state policy because only IT can see the panel. Two
  // rules on top of the bridge's: no-op while the panel is visible, and a
  // cooldown after the user closes/minimizes it — "get out of my way" must
  // stick against a plugin that immediately re-requests.
  const fiasAIGlobal = useGlobalFiasAIOptional();
  const fiasAIGlobalRef = useRef(fiasAIGlobal);
  fiasAIGlobalRef.current = fiasAIGlobal;
  const lastPanelDismissAtRef = useRef(0);
  const panelWasVisibleRef = useRef(false);
  useEffect(() => {
    const visible = !!fiasAIGlobal && fiasAIGlobal.isPanelOpen && !fiasAIGlobal.isMinimized;
    if (panelWasVisibleRef.current && !visible) lastPanelDismissAtRef.current = Date.now();
    panelWasVisibleRef.current = visible;
  }, [fiasAIGlobal]);

  // Manifest ∪ runtime, manifest winning id collisions (it passed publish
  // review; a runtime redefinition of a reviewed action must not stick).
  const mergedAiActions = useMemo(() => {
    const runtime = isTrusted ? runtimeAiDecl : null;
    const merged: PluginDeclaredAiAction[] = [...(manifestAi?.actions ?? [])];
    const ids = new Set(merged.map((a) => a.id));
    for (const action of runtime?.actions ?? []) {
      if (ids.has(action.id)) continue;
      ids.add(action.id);
      merged.push(action);
    }
    if (merged.length > MAX_MERGED_AI_ACTIONS) {
      // Never silent: the trim is visible in telemetry, and deterministic
      // (manifest first) so which verbs survive doesn't depend on timing.
      addBreadcrumb('plugin-ai', 'merged AI action list trimmed to the turn-schema cap', {
        archeId,
        declared: merged.length,
        cap: MAX_MERGED_AI_ACTIONS,
      });
      return merged.slice(0, MAX_MERGED_AI_ACTIONS);
    }
    return merged;
  }, [manifestAi, runtimeAiDecl, isTrusted, archeId]);

  const visibleAiActions = useMemo(
    () => mergedAiActions.filter((a) => aiActionAvailable(a, aiState)).map(toArcheAIAction),
    [mergedAiActions, aiState],
  );

  /** Whether this arche is a registered "open a document in…" destination. */
  // `hasOwnProperty`, not `in`: `in` walks the prototype chain, so `/a/toString`
  // or `/a/constructor` would register as a document-intake target and get an
  // assistant registration for an arche that does not exist.
  const isDocumentIntakeTarget = Object.prototype.hasOwnProperty.call(
    ARCHE_DOCUMENT_INTAKE,
    archeId,
  );

  // ── Arche Handoff relay (host → plugin) ────────────────────────────────
  //
  // The platform half of plugin document intake. Internal arches implement
  // intake themselves (`useArcheHandoffIntake` + `acceptPrefill`); a plugin
  // cannot, because its code runs in the iframe — so the host consumes both
  // paths on its behalf and forwards the payload over the bridge.
  //
  // Being registered in ARCHE_DOCUMENT_INTAKE is what makes an arche a
  // destination; both intake paths already gate on it before reaching here.
  const deliverHandoff = useCallback(
    (payload: ArcheHandoffPayload): boolean => {
      const bridge = bridgeRef.current;
      if (!bridge) return false;

      const readiness = bridge.handoffReadiness();
      // Not up yet — the caller queues and we retry on `onPluginReady`.
      if (readiness === 'not-ready') return false;
      // Built against an SDK predating the handoff lane: it will ignore the
      // message and the lane cannot report that. Say so once and stop, rather
      // than mint a grant and post into silence — "nothing happened" is the
      // exact outcome the capability flag exists to prevent. Reported as
      // HANDLED (true) because the caller's fallback is to navigate here,
      // which lands in the same place and would toast twice.
      if (readiness === 'unsupported') {
        addBreadcrumb('arche-handoff', 'Plugin SDK predates the handoff lane', { archeId });
        toast({
          title: 'This app can’t open files yet',
          description: 'It needs an update from its developer before it can receive files.',
          variant: 'destructive',
        });
        return true;
      }

      // A handoff carries a document REFERENCE, and a plugin cannot read one
      // it was not granted — the consented-read verbs resolve against grant
      // rows, and only the host picker mints those. Without this the whole
      // flow is inert: the user says "open this here", the plugin receives an
      // id, and every read comes back DOCUMENT_NOT_FOUND.
      //
      // So the host mints the grant, as it does for the picker, through the
      // same host-session route (`POST /vault/consents/arche` — a plugin can
      // never mint its own). Choosing this file for this app IS the consent;
      // the grant is per-document, audited, and revocable in Vault → App
      // Access like any other. There is deliberately no second confirmation
      // screen: the user just performed the action, and a modal restating it
      // would only teach people to click through the ones that matter.
      //
      // Returning true means "taken", not "delivered" — `acceptPrefill` must
      // answer synchronously, so the grant and the dispatch finish after it.
      // The grant is a round-trip, so delivery lands after this function
      // returns — which opens two windows the pre-ready queue's latest-wins
      // rule does not cover:
      //
      //   * two handoffs in quick succession can have their grants resolve out
      //     of order, delivering the EARLIER document last and opening a file
      //     the user has already moved on from;
      //   * the arche can change mid-grant, leaving `bridge` destroyed and its
      //     iframe gone, with the dispatch still aimed at it.
      //
      // A monotonic ticket closes both: only the newest request may deliver,
      // and the arche-ID reset bumps it so everything in flight is superseded.
      // The bridge-identity check states the second invariant explicitly rather
      // than relying on the ticket to imply it.
      const ticket = ++handoffSeqRef.current;
      void (async () => {
        try {
          if (payload.kind === 'open_document') {
            await api.post('/vault/consents/arche', {
              granteeArcheId: archeId,
              documentIds: [payload.documentId],
            });
          }
          if (ticket !== handoffSeqRef.current) return;
          if (bridgeRef.current !== bridge) return;
          bridge.dispatchHandoff(payload);
        } catch (err) {
          // Tell the user, because nothing else will: the lane is one-way, so
          // a silent failure looks exactly like "the app ignored my file".
          if (ticket !== handoffSeqRef.current) return;
          addBreadcrumb('arche-handoff', 'Failed to grant handoff document', { archeId });
          toast({
            title: 'Couldn’t open that file here',
            description: isApiErrorCode(err, 'ARCHE_CANNOT_USE_GRANT')
              ? 'This app isn’t set up to open your files.'
              : 'Granting access to the file failed. Try opening it from the app instead.',
            variant: 'destructive',
          });
        }
      })();
      return true;
    },
    [archeId],
  );

  // Navigation path: we're already on the plugin's page (or just landed on
  // it). Queue when the iframe isn't up yet and flush on ready.
  useArcheHandoffIntake(archeId, (payload) => {
    if (!deliverHandoff(payload)) pendingHandoffRef.current = payload;
  });

  useEffect(() => {
    if (!pluginReady) return;
    const queued = pendingHandoffRef.current;
    if (!queued) return;
    pendingHandoffRef.current = null;
    deliverHandoff(queued);
  }, [pluginReady, deliverHandoff]);

  // Register with the global Fias AI window — the same UI plane every
  // internal arche page uses; the assistant sees a plugin arche exactly the
  // way it sees email or image-editor. The description ALWAYS carries the
  // full capability summary (including availableWhen-hidden verbs) so a
  // filtered-out action reads as "needs a document open first", never
  // "can't do that".
  //
  // Registration is NOT gated on having actions alone: an arche registered for
  // document intake must also register, because `acceptPrefill` — the
  // apply-in-place half of a handoff — is delivered through this same entry.
  // The image editor is the worked example: zero declared actions, but it is
  // an "open this file in…" destination.
  useRegisterArcheAI(
    mergedAiActions.length > 0 || isDocumentIntakeTarget
      ? {
          archeId,
          enabled: true,
          archeDescription: [manifestAi?.description, isTrusted ? runtimeAiDecl?.description : null]
            .filter(Boolean)
            .join('\n'),
          actions: visibleAiActions,
          // TRUST BOUNDARY (ADR-017): the snapshot returned here is sent as
          // archeContext.state and interpolated VERBATIM into the assistant's
          // system prompt server-side. For a non-trusted arche that would be
          // an unreviewed free-text channel into the prompt — the exact thing
          // the manifest-only policy exists to prevent — so untrusted state
          // stays HOST-SIDE ONLY: it still drives availableWhen filtering
          // above, but the assistant sees an empty snapshot.
          getStateSnapshot: () => (isTrusted ? aiStateRef.current : {}),
          // In-place path: the assistant hands a document to an arche that is
          // ALREADY open. Returning false (iframe not up yet) is not a
          // failure — the caller falls back to navigation-with-state, which
          // the intake hook above then picks up.
          acceptPrefill: deliverHandoff,
          applyAction: (actionId, params) => {
            // Only declared ids are dispatchable — the model cannot probe the
            // iframe with arbitrary action names.
            if (!mergedAiActions.some((a) => a.id === actionId)) {
              throw new Error(`Unknown action "${actionId}" — the plugin does not declare it.`);
            }
            const bridge = bridgeRef.current;
            if (!bridge) throw new Error('The plugin is not running.');
            // Async relay: resolves on the plugin's ai_action_result; the
            // assistant loop awaits this before telling the model it applied.
            return bridge.dispatchAiAction(actionId, params);
          },
          sessionKey: `plugin:${archeId}`,
        }
      : null,
  );

  // Clean up bridge and pending purchase on unmount
  useEffect(() => {
    return () => {
      if (purchaseResolveRef.current) {
        purchaseResolveRef.current({ success: false, error: 'USER_CANCELED' });
        purchaseResolveRef.current = null;
      }
      if (vaultPickResolveRef.current) {
        vaultPickResolveRef.current({ canceled: true });
        vaultPickResolveRef.current = null;
      }
      if (vaultSaveResolveRef.current) {
        vaultSaveResolveRef.current({ canceled: true });
        vaultSaveResolveRef.current = null;
      }
      bridgeRef.current?.destroy();
    };
  }, []);

  const handleVaultPickResolve = useCallback((result: VaultPickResolution) => {
    vaultPickResolveRef.current?.(result);
    vaultPickResolveRef.current = null;
    setVaultPickRequest(null);
  }, []);

  const handleVaultSaveResolve = useCallback((result: VaultSaveResolution) => {
    vaultSaveResolveRef.current?.(result);
    vaultSaveResolveRef.current = null;
    setVaultSaveRequest(null);
  }, []);

  const handlePurchaseConfirm = useCallback(async () => {
    if (!purchaseProduct || !bridgeRef.current) return;
    setPurchaseLoading(true);
    try {
      // Same-origin call — use cookie auth (the api wrapper handles
      // credentials:'include' + CSRF). No bearer token round-trip
      // needed; this isn't running inside the sandboxed iframe.
      //
      // The store_purchase alias was retired (ADR-012 Phase L Job 3 Step 2); the
      // host executes the confirmed charge through the generic entity_invoke op
      // against the store entity. The server wraps the store result in `{ output }`
      // (mirrors handleStoreOp's entity_invoke store branch in PluginBridge.ts),
      // so unwrap it back to the legacy bare shape the sheet's resolver expects.
      const { data } = await api.post<{ output?: unknown }>(`/plugins/bridge/${archeId}`, {
        type: 'entity_invoke',
        payload: {
          entityId: PLUGIN_STORE_ENTITY_ID,
          payload: { op: 'purchase', productIdentifier: purchaseProduct.productIdentifier },
        },
      });
      const result = ((data as { output?: unknown })?.output ?? data) as {
        success: boolean;
        error?: string;
      };
      purchaseResolveRef.current?.(result);
    } catch {
      purchaseResolveRef.current?.({ success: false, error: 'Purchase failed' });
    } finally {
      setPurchaseLoading(false);
      setPurchaseProduct(null);
      purchaseResolveRef.current = null;
    }
  }, [purchaseProduct, archeId]);

  const handlePurchaseCancel = useCallback(() => {
    purchaseResolveRef.current?.({ success: false, error: 'USER_CANCELED' });
    setPurchaseProduct(null);
    purchaseResolveRef.current = null;
  }, []);

  if (loading) {
    return <ArcheLoadingScreen />;
  }

  if (error) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Plugin Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!pluginConfig) return null;

  if (incompatibleSdk) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Plugin needs update
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-muted-foreground">
              This plugin was built against an older version of the Fias SDK (v
              {incompatibleSdk.sdkVersion}) than the platform currently supports (v
              {incompatibleSdk.minRequired}+). The plugin author needs to rebuild with an updated
              SDK.
            </p>
            <p className="text-sm text-muted-foreground">
              If you're the contributor, run{' '}
              <code className="bg-muted px-1.5 py-0.5 rounded text-xs">
                pnpm up @fias/arche-sdk
              </code>{' '}
              in your plugin project and publish a new version.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (retiredCapability) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Plugin needs update
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-muted-foreground">
              This plugin uses a capability that has been retired from the Fias platform. The plugin
              author needs to rebuild it against a current version of the Fias SDK.
            </p>
            <p className="text-sm text-muted-foreground">
              If you're the contributor, run{' '}
              <code className="bg-muted px-1.5 py-0.5 rounded text-xs">
                pnpm up @fias/arche-sdk
              </code>{' '}
              in your plugin project and publish a new version.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Always the proxied document — `bundleRef` is never used as an iframe `src`.
  // `?? ''` is a fail-safe: an empty iframe, never a third-party origin.
  const iframeProps = { srcDoc: srcdoc ?? '' };

  return (
    <div className="relative w-full h-full min-h-0">
      <iframe
        ref={iframeRef}
        {...iframeProps}
        onLoad={handleIframeLoad}
        sandbox={PLUGIN_IFRAME_SANDBOX}
        allow={PLUGIN_IFRAME_ALLOW}
        referrerPolicy="no-referrer"
        className="w-full border-0 block"
        style={{
          height: iframeHeight != null ? `${iframeHeight}px` : '100%',
          background: 'hsl(var(--background))',
          visibility: iframeReady ? 'visible' : 'hidden',
        }}
        title={`Plugin: ${archeId}`}
      />
      {!iframeReady && (
        <div className="absolute inset-0">
          <ArcheLoadingScreen />
        </div>
      )}
      <PurchaseConfirmationSheet
        open={purchaseProduct !== null}
        product={purchaseProduct}
        userBalance={userBalance}
        loading={purchaseLoading}
        onConfirm={handlePurchaseConfirm}
        onCancel={handlePurchaseCancel}
      />
      <VaultAccessGrantSheet
        open={vaultPickRequest !== null}
        archeId={archeId}
        archeName={archeDisplayName}
        maxDocuments={vaultPickRequest?.maxDocuments ?? 25}
        onResolve={handleVaultPickResolve}
      />
      {vaultSaveRequest !== null && (
        <VaultSaveConsentSheet
          open
          archeName={archeDisplayName}
          folderLabel={vaultFolderLabel}
          request={vaultSaveRequest}
          onResolve={handleVaultSaveResolve}
        />
      )}
      {/* Host-chrome discoverability for the plugin's Fias AI surface —
          rendered only once the iframe is up (a chip over the loading
          screen reads as UI noise) and only when the plugin actually
          declares actions. See FiasAIDiscoveryChip for the rules. */}
      {iframeReady && mergedAiActions.length > 0 && <FiasAIDiscoveryChip archeId={archeId} />}
    </div>
  );
}
